import axios from "axios";
import { ClientListAPI } from "../api/requests/client/list";
import { LocalStorageManager } from "../storage/LocalStorageManager";
import { AppConfig } from "../../AppConfig";
import { LoginAPI } from "../api/requests/auth/login";
import { ForgotPasswordAPI } from "../api/requests/auth/forgot-password";
import { ResetPasswordAPI } from "../api/requests/auth/reset-password";
import { DashboardAPI } from "../api/requests/dashboard";
import { LogoutAPI } from "../api/requests/auth/logout";
import { ChangePasswordAPI } from "../api/requests/account/change-password";
import { BlogCreateAPI } from "../api/requests/blog/create";
import { BlogListAPI } from "../api/requests/blog/list";
import { BlogDetailAPI } from "../api/requests/blog/detail";
import { BlogEditAPI } from "../api/requests/blog/edit";
import { BlogDeleteAPI } from "../api/requests/blog/delete";
import { ClientCreateAPI } from "../api/requests/client/create";
import { ClientDetailAPI } from "../api/requests/client/detail";
import { ClientEditAPI } from "../api/requests/client/edit";
import { FeatureCreateAPI } from "../api/requests/feature/create";
import { FeatureListAPI } from "../api/requests/feature/list";
import { FeatureDetailAPI } from "../api/requests/feature/detail";
import { FeatureEditAPI } from "../api/requests/feature/edit";
import { FeatureDeleteAPI } from "../api/requests/feature/delete";
import { HomepageImageCreateAPI } from "../api/requests/homepage_image/create";
import { HomepageImageListAPI } from "../api/requests/homepage_image/list";
import { MemberCreateAPI } from "../api/requests/member/create";
import { MemberListAPI } from "../api/requests/member/list";
import { MemberDetailAPI } from "../api/requests/member/detail";
import { MemberEditAPI } from "../api/requests/member/edit";
import { MemberDeleteAPI } from "../api/requests/member/delete";
import { OwnerCreateAPI } from "../api/requests/owner/create";
import { OwnerListAPI } from "../api/requests/owner/list";
import { OwnerDetailAPI } from "../api/requests/owner/detail";
import { OwnerEditAPI } from "../api/requests/owner/edit";
import { OwnerDeleteAPI } from "../api/requests/owner/delete";
import { PopularPlaceCreateAPI } from "../api/requests/popular_place/create";
import { PopularPlaceListAPI } from "../api/requests/popular_place/list";
import { PopularPlaceDetailAPI } from "../api/requests/popular_place/detail";
import { PopularPlaceEditAPI } from "../api/requests/popular_place/edit";
import { PopularPlaceDeleteAPI } from "../api/requests/popular_place/delete";
import { CityListAPI } from "../api/requests/property/city";
import { DistrictListAPI } from "../api/requests/property/district";
import { CommuneListAPI } from "../api/requests/property/commune";
import { PropertyCategoryAPI } from "../api/requests/property/property-category";
import { PropertyImageListAPI } from "../api/requests/property/property-image";
import { PropertyOwnerAPI } from "../api/requests/property/property-owner";
import { PropertyTypeListAPI } from "../api/requests/property/property-type";
import { PropertyTypeByCategoryAPI } from "../api/requests/property/property-type-by-category";
import { PropertyListAPI } from "../api/requests/property/list";
import { PropertyExportAPI } from "../api/requests/property/export";
import { PropertyDetailAPI } from "../api/requests/property/detail";
import { PropertyDeleteAPI } from "../api/requests/property/delete";
import { PropertyEditAPI } from "../api/requests/property/edit";
import { PropertyCreateAPI } from "../api/requests/property/create";
import { handleSessionExpired } from "../../utils/auth";

const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === "true";
const API_BASE_URL = process.env.REACT_APP_API_BASE ?? "";

// Flag to prevent multiple refresh attempts
let isShowingSessionExpiredModal = false;

// Function to reset the modal flag
export const resetSessionExpiredModalFlag = () => {
  isShowingSessionExpiredModal = false;
};

export const axiosInstance = axios.create({
  baseURL: USE_MOCK_API ? "" : API_BASE_URL,
  useMockApi: USE_MOCK_API,
  displayError: true,
});

axiosInstance.interceptors.request.use((config) => {
  const accessToken = LocalStorageManager.Instance.getObject<string>(
    AppConfig.Instance.LocalStorageKey["accessToken"],
  );
  config.headers = config.headers || {};
  if (accessToken) {
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor to handle token expiration
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't tried to show the modal yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Prevent multiple modals from showing
      if (isShowingSessionExpiredModal) {
        // Just return the original error without showing modal again
        return Promise.reject(error);
      }

      isShowingSessionExpiredModal = true;

      // Show session expired modal
      handleSessionExpired();

      // Return the original error - the calling code should handle it gracefully
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);




export const domainBaseURLOption = () => {
  if (process.env.REACT_APP_USE_MOCK_API === "false") {
    return {
      ...axiosInstance.options,
      baseURL: process.env.REACT_APP_API_BASE,
    };
  }
  return { ...axiosInstance.options };
};

const loginApi = new LoginAPI(axiosInstance);
const forgotPasswordApi = new ForgotPasswordAPI(axiosInstance);
const resetPasswordApi = new ResetPasswordAPI(axiosInstance);
const logoutApi = new LogoutAPI(axiosInstance);
const changePasswordApi = new ChangePasswordAPI(axiosInstance);

const dashboardApi = new DashboardAPI(axiosInstance);

const blogCreateApi = new BlogCreateAPI(axiosInstance);
const blogListApi = new BlogListAPI(axiosInstance);
const blogDetailApi = new BlogDetailAPI(axiosInstance);
const blogEditApi = new BlogEditAPI(axiosInstance);
const blogDeleteApi = new BlogDeleteAPI(axiosInstance);

const clientCreateApi = new ClientCreateAPI(axiosInstance);
const clientListApi = new ClientListAPI(axiosInstance);
const clientDetailApi = new ClientDetailAPI(axiosInstance);
const clientEditApi = new ClientEditAPI(axiosInstance);

const featureCreateApi = new FeatureCreateAPI(axiosInstance);
const featureListApi = new FeatureListAPI(axiosInstance);
const featureDetailApi = new FeatureDetailAPI(axiosInstance);
const featureEditApi = new FeatureEditAPI(axiosInstance);
const featureDeleteApi = new FeatureDeleteAPI(axiosInstance);

const homepageImageCreateApi = new HomepageImageCreateAPI(axiosInstance);
const homepageImageListApi = new HomepageImageListAPI(axiosInstance);

const memberCreateApi = new MemberCreateAPI(axiosInstance);
const memberListApi = new MemberListAPI(axiosInstance);
const memberDetailApi = new MemberDetailAPI(axiosInstance);
const memberEditApi = new MemberEditAPI(axiosInstance);
const memberDeleteApi = new MemberDeleteAPI(axiosInstance);

const ownerCreateApi = new OwnerCreateAPI(axiosInstance);
const ownerListApi = new OwnerListAPI(axiosInstance);
const ownerDetailApi = new OwnerDetailAPI(axiosInstance);
const ownerEditApi = new OwnerEditAPI(axiosInstance);
const ownerDeleteApi = new OwnerDeleteAPI(axiosInstance);

const popularPlaceCreateApi = new PopularPlaceCreateAPI(axiosInstance);
const popularPlaceListApi = new PopularPlaceListAPI(axiosInstance);
const popularPlaceDetailApi = new PopularPlaceDetailAPI(axiosInstance);
const popularPlaceEditApi = new PopularPlaceEditAPI(axiosInstance);
const popularPlaceDeleteApi = new PopularPlaceDeleteAPI(axiosInstance)

const PropertyListApi = new PropertyListAPI(axiosInstance);
const PropertyExportApi = new PropertyExportAPI(axiosInstance);
const PropertyDetailApi = new PropertyDetailAPI(axiosInstance);
const PropertyEditApi = new PropertyEditAPI(axiosInstance);
const PropertyCreateApi = new PropertyCreateAPI(axiosInstance);
const PropertyDeleteApi = new PropertyDeleteAPI(axiosInstance);

const cityListApi = new CityListAPI(axiosInstance);
const getDistrictListApi = (city_id: string) => new DistrictListAPI(axiosInstance, city_id);
const getCommuneListApi = (district_id: string) => new CommuneListAPI(axiosInstance, district_id);
const propertyCategoryApi = new PropertyCategoryAPI(axiosInstance);
const propertyImageListApi = new PropertyImageListAPI(axiosInstance);
const propertyOwnerApi = new PropertyOwnerAPI(axiosInstance);
const propertyTypeListApi = new PropertyTypeListAPI(axiosInstance);
const getPropertyTypeByCategoryApi = (category_id: string) => new PropertyTypeByCategoryAPI(axiosInstance, category_id);

export {
  loginApi,
  forgotPasswordApi,
  resetPasswordApi,
  logoutApi,
  changePasswordApi,
  dashboardApi,
  blogCreateApi,
  blogDetailApi,
  blogListApi,
  blogEditApi,
  blogDeleteApi,
  clientCreateApi,
  clientListApi,
  clientDetailApi,
  clientEditApi,
  featureCreateApi,
  featureListApi,
  featureDetailApi,
  featureEditApi,
  featureDeleteApi,
  homepageImageCreateApi,
  homepageImageListApi,
  memberCreateApi,
  memberListApi,
  memberDetailApi,
  memberEditApi,
  memberDeleteApi,
  ownerCreateApi,
  ownerListApi,
  ownerEditApi,
  ownerDetailApi,
  ownerDeleteApi,
  popularPlaceCreateApi,
  popularPlaceDeleteApi,
  popularPlaceDetailApi,
  popularPlaceEditApi,
  popularPlaceListApi,
  cityListApi,
  propertyOwnerApi,
  propertyCategoryApi,
  propertyImageListApi,
  propertyTypeListApi,
  PropertyListApi,
  PropertyExportApi,
  PropertyDetailApi,
  PropertyDeleteApi,
  PropertyCreateApi,
  PropertyEditApi,
}
export { getDistrictListApi, getCommuneListApi, getPropertyTypeByCategoryApi };